import { formatCurrency } from './formatMoeda';

export function calculateTotal(quantidade, vlrUnit) {
  console.log(
    'Calculando total com quantidade:',
    quantidade,
    'e valor unitário:',
    vlrUnit
  );
  const quantidadeNum = parseFloat(quantidade) || 0;
  const vlrUnitNum = vlrUnit || 0;
  return formatCurrency(quantidadeNum * vlrUnitNum);
}

/**
 * Calcula o saldo entre orçamento e despesas pagas
 * @param {Array} orcamentos - Array de orçamentos
 * @param {Array} despesas - Array de despesas
 * @param {Object} filtros - Filtros para cálculo (idPrograma, idAcao, idSubAcao, idItem)
 * @param {boolean} incluirDespesasSemPrograma - Se deve incluir despesas sem programa
 * @returns {Object} - Objeto com totais e saldo
 */
export function calculateSaldoOrcamentoDespesa(
  orcamentos,
  despesas,
  filtros = {},
  incluirDespesasSemPrograma = false
) {
  // Filtrar orçamentos baseado nos filtros fornecidos
  const orcamentosFiltrados = orcamentos.filter(orc => {
    if (filtros.idPrograma && orc.idPrograma !== filtros.idPrograma)
      return false;
    if (filtros.idAcao && orc.idAcao !== filtros.idAcao) return false;
    if (filtros.idSubAcao && orc.idSubAcao !== filtros.idSubAcao) return false;
    if (filtros.idItem && orc.idItem !== filtros.idItem) return false;
    return true;
  });

  // Filtrar despesas baseado nos filtros fornecidos (apenas despesas pagas)
  const despesasFiltradas = despesas.filter(desp => {
    // Considerar apenas despesas que foram pagas (têm data de pagamento)
    if (!desp.dataPagamento) return false;

    // Excluir despesas anuladas
    if (desp.dataAnulacao !== null && desp.dataAnulacao !== undefined && String(desp.dataAnulacao).trim() !== '') {
      return false;
    }

    // Se não incluir despesas sem programa e a despesa não tem programa, excluir
    if (
      !incluirDespesasSemPrograma &&
      (!desp.idPrograma || desp.idPrograma === null)
    ) {
      return false;
    }

    // Se há filtro de programa mas a despesa não tem programa, incluir apenas se explicitamente solicitado
    if (filtros.idPrograma) {
      if (!desp.idPrograma || desp.idPrograma === null) {
        return incluirDespesasSemPrograma;
      }
      if (desp.idPrograma !== filtros.idPrograma) return false;
    }

    if (filtros.idAcao && desp.idAcao !== filtros.idAcao) return false;
    if (filtros.idSubAcao && desp.idSubAcao !== filtros.idSubAcao) return false;
    if (filtros.idItem && desp.idItem !== filtros.idItem) return false;
    return true;
  });

  // Calcular total orçado
  const totalOrcado = orcamentosFiltrados.reduce((total, orc) => {
    return total + (parseFloat(orc.valor) || 0);
  }, 0);

  // Calcular total pago (despesas)
  const totalPago = despesasFiltradas.reduce((total, desp) => {
    return total + (parseFloat(desp.valor) || 0);
  }, 0);

  // Calcular saldo (orçado - pago)
  const saldo = totalOrcado - totalPago;

  // Calcular percentual executado
  const percentualExecutado =
    totalOrcado > 0 ? (totalPago / totalOrcado) * 100 : 0;

  return {
    totalOrcado,
    totalPago,
    saldo,
    percentualExecutado,
    quantidadeOrcamentos: orcamentosFiltrados.length,
    quantidadeDespesasPagas: despesasFiltradas.length,
  };
}

/**
 * Calcula saldo agrupado por programa
 * @param {Array} orcamentos - Array de orçamentos
 * @param {Array} despesas - Array de despesas
 * @param {boolean} incluirDespesasSemPrograma - Se deve incluir despesas sem programa
 * @returns {Array} - Array com saldo por programa
 */
export function calculateSaldoPorPrograma(
  orcamentos,
  despesas,
  incluirDespesasSemPrograma = true
) {
  const programas = {};
  let despesasSemPrograma = {
    idPrograma: null,
    descricaoPrograma: 'Despesas sem Programa',
    totalOrcado: 0,
    totalPago: 0,
  };

  // Agrupar orçamentos por programa
  orcamentos.forEach(orc => {
    if (orc.idPrograma) {
      if (!programas[orc.idPrograma]) {
        programas[orc.idPrograma] = {
          idPrograma: orc.idPrograma,
          descricaoPrograma: orc.descricaoPrograma,
          totalOrcado: 0,
          totalPago: 0,
        };
      }
      programas[orc.idPrograma].totalOrcado += parseFloat(orc.valor) || 0;
    }
  });

  // Somar despesas pagas por programa
  despesas.forEach(desp => {
    if (desp.dataPagamento) {
      // Excluir despesas anuladas
      const isAnulada = desp.dataAnulacao !== null && desp.dataAnulacao !== undefined && String(desp.dataAnulacao).trim() !== '';
      if (isAnulada) return;
      
      if (desp.idPrograma && programas[desp.idPrograma]) {
        programas[desp.idPrograma].totalPago += parseFloat(desp.valor) || 0;
      } else if (!desp.idPrograma || desp.idPrograma === null) {
        // Despesa sem programa
        despesasSemPrograma.totalPago += parseFloat(desp.valor) || 0;
      }
    }
  });

  // Calcular saldo e percentual para cada programa
  const resultados = Object.values(programas).map(prog => ({
    ...prog,
    saldo: prog.totalOrcado - prog.totalPago,
    percentualExecutado:
      prog.totalOrcado > 0 ? (prog.totalPago / prog.totalOrcado) * 100 : 0,
  }));

  // Incluir despesas sem programa se solicitado e se existirem
  if (incluirDespesasSemPrograma && despesasSemPrograma.totalPago > 0) {
    despesasSemPrograma.saldo =
      despesasSemPrograma.totalOrcado - despesasSemPrograma.totalPago;
    despesasSemPrograma.percentualExecutado =
      despesasSemPrograma.totalOrcado > 0
        ? (despesasSemPrograma.totalPago / despesasSemPrograma.totalOrcado) *
          100
        : despesasSemPrograma.totalPago > 0
        ? 100
        : 0; // 100% se só tem despesas, 0% se nada
    resultados.push(despesasSemPrograma);
  }

  return resultados;
}

/**
 * Calcula saldo agrupado por ação
 * @param {Array} orcamentos - Array de orçamentos
 * @param {Array} despesas - Array de despesas
 * @param {number} idPrograma - ID do programa para filtrar
 * @param {boolean} incluirDespesasSemAcao - Se deve incluir despesas sem ação
 * @returns {Array} - Array com saldo por ação
 */
export function calculateSaldoPorAcao(
  orcamentos,
  despesas,
  idPrograma = null,
  incluirDespesasSemAcao = true
) {
  const acoes = {};
  let despesasSemAcao = {
    idPrograma: idPrograma,
    idAcao: null,
    descricaoPrograma: idPrograma ? 'Programa ' + idPrograma : 'Sem Programa',
    descricaoAcao: 'Despesas sem Ação',
    totalOrcado: 0,
    totalPago: 0,
  };

  // Filtrar por programa se fornecido
  const orcamentosFiltrados = idPrograma
    ? orcamentos.filter(orc => orc.idPrograma === idPrograma)
    : orcamentos;

  const despesasFiltradas = idPrograma
    ? despesas.filter(desp => desp.idPrograma === idPrograma)
    : despesas;

  // Agrupar orçamentos por ação
  orcamentosFiltrados.forEach(orc => {
    if (orc.idAcao) {
      const chaveAcao = `${orc.idPrograma}-${orc.idAcao}`;
      if (!acoes[chaveAcao]) {
        acoes[chaveAcao] = {
          idPrograma: orc.idPrograma,
          idAcao: orc.idAcao,
          descricaoPrograma: orc.descricaoPrograma,
          descricaoAcao: orc.descricaoAcao,
          totalOrcado: 0,
          totalPago: 0,
        };
      }
      acoes[chaveAcao].totalOrcado += parseFloat(orc.valor) || 0;
    }
  });

  // Somar despesas pagas por ação
  despesasFiltradas.forEach(desp => {
    if (desp.dataPagamento) {
      // Excluir despesas anuladas
      const isAnulada = desp.dataAnulacao !== null && desp.dataAnulacao !== undefined && String(desp.dataAnulacao).trim() !== '';
      if (isAnulada) return;
      
      if (desp.idAcao) {
        const chaveAcao = `${desp.idPrograma}-${desp.idAcao}`;
        if (acoes[chaveAcao]) {
          acoes[chaveAcao].totalPago += parseFloat(desp.valor) || 0;
        }
      } else if (!desp.idAcao || desp.idAcao === null) {
        // Despesa sem ação
        despesasSemAcao.totalPago += parseFloat(desp.valor) || 0;
      }
    }
  });

  // Calcular saldo e percentual para cada ação
  const resultados = Object.values(acoes).map(acao => ({
    ...acao,
    saldo: acao.totalOrcado - acao.totalPago,
    percentualExecutado:
      acao.totalOrcado > 0 ? (acao.totalPago / acao.totalOrcado) * 100 : 0,
  }));

  // Incluir despesas sem ação se solicitado e se existirem
  if (incluirDespesasSemAcao && despesasSemAcao.totalPago > 0) {
    despesasSemAcao.saldo =
      despesasSemAcao.totalOrcado - despesasSemAcao.totalPago;
    despesasSemAcao.percentualExecutado =
      despesasSemAcao.totalOrcado > 0
        ? (despesasSemAcao.totalPago / despesasSemAcao.totalOrcado) * 100
        : despesasSemAcao.totalPago > 0
        ? 100
        : 0; // 100% se só tem despesas, 0% se nada
    resultados.push(despesasSemAcao);
  }

  return resultados;
}

/**
 * Calcula saldo total incluindo todas as despesas (com e sem programa)
 * @param {Array} orcamentos - Array de orçamentos
 * @param {Array} despesas - Array de despesas
 * @returns {Object} - Objeto com totais incluindo despesas sem programa
 */
export function calculateSaldoTotalComDespesasSemPrograma(
  orcamentos,
  despesas
) {
  // Calcular saldo apenas dos programas
  const saldoComPrograma = calculateSaldoOrcamentoDespesa(
    orcamentos,
    despesas,
    {},
    false
  );

  // Calcular despesas sem programa
  const despesasSemPrograma = despesas.filter(
    desp => {
      // Excluir despesas anuladas
      const isAnulada = desp.dataAnulacao !== null && desp.dataAnulacao !== undefined && String(desp.dataAnulacao).trim() !== '';
      if (isAnulada) return false;
      
      return desp.dataPagamento && (!desp.idPrograma || desp.idPrograma === null);
    }
  );

  const totalDespesasSemPrograma = despesasSemPrograma.reduce((total, desp) => {
    return total + (parseFloat(desp.valor) || 0);
  }, 0);

  return {
    // Totais dos programas
    totalOrcadoProgramas: saldoComPrograma.totalOrcado,
    totalPagoProgramas: saldoComPrograma.totalPago,
    saldoProgramas: saldoComPrograma.saldo,
    percentualExecutadoProgramas: saldoComPrograma.percentualExecutado,

    // Despesas sem programa
    totalDespesasSemPrograma,
    quantidadeDespesasSemPrograma: despesasSemPrograma.length,

    // Totais gerais
    totalOrcadoGeral: saldoComPrograma.totalOrcado, // Só tem orçamento nos programas
    totalPagoGeral: saldoComPrograma.totalPago + totalDespesasSemPrograma,
    saldoGeral:
      saldoComPrograma.totalOrcado -
      (saldoComPrograma.totalPago + totalDespesasSemPrograma),
    percentualExecutadoGeral:
      saldoComPrograma.totalOrcado > 0
        ? ((saldoComPrograma.totalPago + totalDespesasSemPrograma) /
            saldoComPrograma.totalOrcado) *
          100
        : 0,

    // Contadores
    quantidadeOrcamentos: saldoComPrograma.quantidadeOrcamentos,
    quantidadeDespesasPagasTotal:
      saldoComPrograma.quantidadeDespesasPagas + despesasSemPrograma.length,
  };
}
